import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "@/lib/public-icons";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSession } from "@/lib/host/auth-client";
import { orpc } from "@/lib/orpc";
import { DemographicsSection } from "./-components/demographics-section";
import { MedicationsSection } from "./-components/medications-section";
import { OrdersSection } from "./-components/orders-section";
import { PartnerShell } from "./-components/partner-shell";
import { PasswordGate } from "./-components/password-gate";
import { PlanOfCareSection } from "./-components/plan-of-care-section";
import { RecordsSection } from "./-components/records-section";
import { ResidentChartHeader } from "./-components/resident-chart-header";
import { TherapySection } from "./-components/therapy-section";
import { VitalsSection } from "./-components/vitals-section";

export const Route = createFileRoute("/partner-portal/$residentId")({
  component: PartnerResidentPage,
});

const TABS = [
  { value: "demographics", label: "Demographics" },
  { value: "physician-poc", label: "Physician POC" },
  { value: "hnp", label: "H&P" },
  { value: "vitals", label: "Vitals" },
  { value: "orders", label: "Orders" },
  { value: "medications", label: "Medications" },
  { value: "therapy", label: "Therapies" },
] as const;

/** One resident's chart, in the seven sections an agency is granted. */
function PartnerResidentPage() {
  const { residentId } = Route.useParams();
  const session = useSession();
  const enabled = Boolean(session.data);
  const [tab, setTab] = useState<string>("demographics");

  const me = useQuery({ ...orpc.partnerPortal.me.queryOptions({}), retry: false, enabled });
  const residents = useQuery({
    ...orpc.partnerPortal.residents.queryOptions({}),
    retry: false,
    enabled,
  });

  if (!session.data) {
    return (
      <PartnerShell>
        <Card>
          <CardContent className="p-8 text-center">
            <Button asChild>
              <Link to="/login" search={{ next: `/partner-portal/${residentId}` }}>
                Sign in
              </Link>
            </Button>
          </CardContent>
        </Card>
      </PartnerShell>
    );
  }

  if (me.isPending) {
    return (
      <PartnerShell name={session.data.user.name}>
        <Skeleton className="h-64 w-full" />
      </PartnerShell>
    );
  }

  if (me.isError) {
    return (
      <PartnerShell name={session.data.user.name}>
        <EmptyState title="This portal is not open to you" description={errorWords(me.error)} />
      </PartnerShell>
    );
  }

  const institution = me.data.institution.name;
  const name = me.data.user.name;

  // Also here, not only on the list: a bookmark straight to a chart would
  // otherwise walk past the temporary password somebody else still knows.
  if (me.data.user.mustResetPassword) {
    return (
      <PartnerShell institution={institution} name={name}>
        <PasswordGate />
      </PartnerShell>
    );
  }

  if (residents.isPending) {
    return (
      <PartnerShell institution={institution} name={name}>
        <Skeleton className="h-64 w-full" />
      </PartnerShell>
    );
  }

  if (residents.isError) {
    return (
      <PartnerShell institution={institution} name={name}>
        <ReadFailed
          message="Could not load your residents."
          error={residents.error}
          onRetry={() => void residents.refetch()}
          retrying={residents.isFetching}
        />
      </PartnerShell>
    );
  }

  const resident = (residents.data ?? []).find((row) => row.id === residentId);
  if (!resident) {
    // The list is the whole of what this agency holds, so a resident missing
    // from it is a resident it was not given. Said the same way whether the
    // link was ended this morning or never existed.
    return (
      <PartnerShell institution={institution} name={name}>
        <EmptyState
          title="This resident is not shared with you"
          description="Everyone your company has been given is on the main page."
        />
      </PartnerShell>
    );
  }

  // The building's clock, not the reader's: an agency office two states away
  // must read a vital sign at the hour the nurse charted it.
  const timezone = resident.facilityTimezone ?? "UTC";

  return (
    <PartnerShell institution={institution} name={name}>
      <Link
        className="text-muted-foreground hover:text-foreground mb-6 flex items-center gap-1 text-sm"
        to="/partner-portal"
      >
        <ChevronLeft className="size-4" />
        All residents
      </Link>

      <ResidentChartHeader resident={resident} />

      {/* Each section fetches on its own, and an unopened tab is not mounted:
          every one of these reads writes a disclosure entry, so a trail saying
          the agency opened the H&P has to mean they opened the H&P. */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap">
          {TABS.map((entry) => (
            <TabsTrigger key={entry.value} value={entry.value}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="demographics">
          <DemographicsSection residentId={residentId} timezone={timezone} />
        </TabsContent>
        <TabsContent value="physician-poc">
          <PlanOfCareSection residentId={residentId} timezone={timezone} />
        </TabsContent>
        <TabsContent value="hnp">
          <RecordsSection residentId={residentId} timezone={timezone} />
        </TabsContent>
        <TabsContent value="vitals">
          <VitalsSection residentId={residentId} timezone={timezone} />
        </TabsContent>
        <TabsContent value="orders">
          <OrdersSection residentId={residentId} timezone={timezone} />
        </TabsContent>
        <TabsContent value="medications">
          <MedicationsSection residentId={residentId} timezone={timezone} />
        </TabsContent>
        <TabsContent value="therapy">
          <TherapySection residentId={residentId} timezone={timezone} />
        </TabsContent>
      </Tabs>
    </PartnerShell>
  );
}
